import React, {
  memo, useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';

import {
  Download,
  DownloadList,
  Collections,
} from '@picsio/ui/dist/icons';
import ua from '../../ua';
import picsioConfig from '../../../../../config';
import ErrorBoundary from '../ErrorBoundary';
import localization from '../../shared/strings';
import ToolbarSort from '../Sort';
import CatalogViewMode from '../CatalogViewMode';
import Tooltip from '../Tooltip';

import Breadcrumbs from '../Breadcrumbs';
import Search from '../Search';
import Button from './Button';
import Group from './Group';
import BrandedLogo from './BrandedLogo';
import ProofingContacts from './ProofingContacts';

const { isProofing } = picsioConfig;
const { websiteConfig } = window;

const {
  blogLinkShow,
  contactsShow,
  emailShow,
  fbLinkShow,
  instagramLinkShow,
  phoneShow,
  logoUrl,
  twitterLinkShow,
  user,
  newSearchFilters,
} = websiteConfig;

const permissions = {
  contacts: {
    phone: phoneShow,
    email: emailShow,
    blogUrl: blogLinkShow,
    facebookUrl: fbLinkShow,
    instagramUrl: instagramLinkShow,
    twitterUrl: twitterLinkShow,
  },
};

const showContacts = phoneShow || emailShow || blogLinkShow || fbLinkShow || instagramLinkShow || twitterLinkShow;

function ToolbarCatalogProofingTop(props) {
  const {
    downloadListItemsLength = 0,
    downloadListOpened = false,
    handleDownload,
    handleDownloadList,
    handleToggleTree,
    openedTree = 'collections',
    selectedItemsLength = 0,
    sortType,
    handleChangeSort,
  } = props;

  const { access } = picsioConfig;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  /** Update window width on resize */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = useMemo(
    () => ua.browser.isNotDesktop() || windowWidth < 1024,
    [windowWidth],
  );

  return (
    <div className="toolbar toolbarCatalogTop">
      <If condition={access.tagsTreeShow}>
        <>
          <Group>
            <Button
              id="button-proofingTree"
              icon={() => <Collections />}
              onClick={handleToggleTree}
              additionalClass={openedTree === 'collections' ? 'active' : null}
            />
          </Group>
          <If condition={logoUrl}>
            <Group>
              <BrandedLogo src={logoUrl} rounded />
            </Group>
          </If>
          <Group additionalClass="wrapperBreadcrumbs">
            <ErrorBoundary>
              <Breadcrumbs />
            </ErrorBoundary>
          </Group>
          <If condition={access.searchShow && !picsioConfig.isPim && newSearchFilters}>
            <Group additionalClass="toolbarSearchWrapper">
              <div id="search-input" />
            </Group>
          </If>
        </>
      </If>
      <If condition={!access.tagsTreeShow}>
        <>
          <If condition={logoUrl}>
            <Group>
              <BrandedLogo src={logoUrl} rounded />
            </Group>
          </If>
          <Group additionalClass="toolbarGroup-first">
            <span className="proofingTitle">{access.customGalleryTitle}</span>
          </Group>
          <If condition={access.searchShow && !picsioConfig.isPim && newSearchFilters}>
            <Group additionalClass="toolbarSearchWrapper">
              <div id="search-input" />
            </Group>
          </If>
        </>
      </If>
      <Group>
        <If condition={access.searchShow && !picsioConfig.isPim && (!newSearchFilters || isMobile)}>
          <ErrorBoundary>
            <Search
              hide={{
                keywords: isProofing,
                labels: isProofing,
                flags: isProofing && !access.flagShow,
                colors: isProofing && !access.colorShow,
                rating: isProofing && !access.ratingShow,
                btnSaveThisSearch: isProofing,
                customFieldsAdd: isProofing,
              }}
            />
          </ErrorBoundary>
          <Tooltip
            content={localization.SEARCH.view}
            placement="top"
          >
            <div>
              <CatalogViewMode openOnClick leftFilter />
            </div>
          </Tooltip>
          <Tooltip
            content={localization.SEARCH.sort}
            placement="top"
          >
            <div>
              <ToolbarSort
                sortType={sortType}
                changeSort={handleChangeSort}
                openOnClick
              />
            </div>
          </Tooltip>
        </If>
        <If condition={(downloadListItemsLength > 0 || downloadListOpened)}>
          <Button
            id="button-downloadList"
            icon={() => <DownloadList />}
            iconSize="xl"
            tooltip={localization.TOOLBARS.titleDownloadDialog}
            tooltipPosition="bottom"
            onClick={handleDownloadList}
            additionalClass={downloadListOpened ? 'active' : null}
            counter={downloadListItemsLength}
          />
        </If>
        <If condition={contactsShow && showContacts}>
          <ProofingContacts user={user} permissions={permissions.contacts} />
        </If>
        <If condition={access.download || (websiteConfig.downloadSingleFile && selectedItemsLength > 1)}>
          <Button
            id="button-proofingDownload"
            icon={() => <Download />}
            tooltip={
              selectedItemsLength > 0
                ? localization.TOOLBARS.titleDownloadSelectedAssets(selectedItemsLength)
                : localization.TOOLBARS.titleDownloadAssetsAsArchive
            }
            tooltipPosition="bottom"
            onClick={handleDownload}
          />
        </If>
      </Group>
    </div>
  );
}

ToolbarCatalogProofingTop.propTypes = {
  downloadListItemsLength: PropTypes.number,
  downloadListOpened: PropTypes.bool,
  handleDownload: PropTypes.func.isRequired,
  handleDownloadList: PropTypes.func.isRequired,
  handleToggleTree: PropTypes.func.isRequired,
  openedTree: PropTypes.string,
  selectedItemsLength: PropTypes.number,
};

export default memo(ToolbarCatalogProofingTop);
